import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import MetaDataHelmet from '../../../Shared/MetaDataHelmet'
import RulesPageEmbedBadges from './RulesPageEmbedBadges'

function RulesPage({ competitionSlug }) {
  const metaTitle = I18n.t(`competition.${competitionSlug}.meta.title.rules`)
  const metaDescription = I18n.t(`competition.${competitionSlug}.meta.description.rules`)
  const pageDescription = I18n.t(`competition.${competitionSlug}.rules.page_description`)
  const competitionTitle = I18n.t(`competition.${competitionSlug}.title`)
  const categoriesNumber = I18n.t(`competition.${competitionSlug}.categories.number`)
  const rules = I18n.t(`competition.${competitionSlug}.rules`)

  return (
    <div className="competition-rules-page">
      <MetaDataHelmet title={metaTitle} description={metaDescription} />

      <section className="content-section section--rules">
        <div className="container-fixed">
          <h2 className="content-section__title">Participation Rules</h2>
          <p className="content-section__subtitle">{pageDescription}</p>

          <div className="rules-section rules__how-to-enter">
            <h3 className="rules-section__title">How to enter?</h3>
            <div className="rules-section__title is-smaller">
              1. Select your artwork and choose a relevant category for it to compete in
            </div>
            <div className="rules-section__description">
              <p>
                <span dangerouslySetInnerHTML={{ __html: rules.how_to_enter.first_step_html }} />
              </p>
            </div>
            <div className="rules-section__title is-smaller">
              2. Add a tag “Participant of the <a href="https://www.cgtrader.com" className="link--primary-dark">CGTrader</a> {competitionTitle}” on your portfolio website
            </div>
            <div className="rules-section__description">
              <div>
                There are 3 ways you can add a tag on your portfolio website:
                <ol>
                  <li>
                    Add a text note “Participant of the <a href="https://www.cgtrader.com" className="link--primary-dark">CGTrader</a> {competitionTitle}” and include
                    a clickable link to CGTrader's website <a href="https://www.cgtrader.com" className="link--primary-dark">CGTrader.com</a>
                  </li>
                  <li>
                    Place an image of the Competition badge (available for download <a href={rules.how_to_enter.badges_link} download className="link--primary-dark">here</a>) and include
                    a clickable link to CGTrader's website
                  </li>
                  <li>
                    Select one of the embed codes listed below and copy & paste it into your website HTML
                  </li>
                </ol>

                <RulesPageEmbedBadges
                  badges={rules.badges}
                  competitionTitle={competitionTitle}
                />

              </div>
              <p>
                The Portfolio website is your personal website or/and an online portfolio on design communities e.g. DeviantArt, Behance, Dribbble,
                etc. A note can be added in the description of your entry or anywhere on your website, e.g. your blog, resume, awards/publications or any other page.
              </p>
            </div>
            <div className="rules-section__title is-smaller">
              3. Fill in the submission form and start competing for the prizes
            </div>
            <div className="rules-section__description">
              <p>
                {rules.how_to_enter.third_step}
              </p>
            </div>
            <small className="text-semibold">Note: Participation is free of charge. No purchase necessary to enter.</small>
          </div>

          <div className="rules-section rules__key-dates">
            <h3 className="rules-section__title">Key dates</h3>
            <ul>
              <li className="text-semibold">Call for submissions: {rules.key_dates.submission} (11:59 PM Pacific Time (“PT”))</li>
              <li>Evaluation Round: <span className="text-semibold">{rules.key_dates.evaluation}</span></li>
              <li>Winner Announcement Online: <span className="text-semibold">{rules.key_dates.winners}</span></li>
            </ul>
          </div>

          <div className="rules-section rules__general-rules">
            <h3 className="rules-section__title is-smaller">General rules</h3>
            <p>
              The following are the official rules (“Rules”) for the “CGTrader {competitionTitle}” (“Competition”).
              In order to enter the Competition, the participant (“Participant”) must agree to these Rules.
              The Participant agrees that the submission of the online form with an artwork image file attached and
              the placement ("Entry") of a URL with a special tag (including the link) constitutes the Participant’s
              agreement to these Rules. The Competition is organized and sponsored by CGTrader, UAB (“Organizer”) as
              well as partners from {rules.sponsors} (“Sponsors”).
            </p>
            <p>
              The Organizer has the right to verify the accuracy of any information provided by the Participant to
              ensure a smooth and fair competition to all related parties, and to otherwise investigate the eligibility
              of any Participant to take part in the Competition.
            </p>
            <p>
              All the copyrights during and after the competition belong to the Participant. Submitted artworks will be
              showcased in the competition gallery (cgtrader.com/{competitionSlug}/gallery) and may be used for
              the marketing purposes during the competition.
            </p>
          </div>

          <div className="rules-section rules__entry-validation">
            <h3 className="rules-section__title is-smaller">Contest entry validation</h3>
            <ul>
              <li>{rules.entry_validation.first_point}</li>
              <li>The Participant is allowed to submit up to {rules.entry_validation.max_submissions} different artworks per category. All latter excessive submissions will be disqualified from the Competition.</li>
              <li>Artwork/s should be high-resolution image/s - 1280 pixels or more on the long edge (e.g. 1280x720 or 720x1280).</li>
              <li>File size should not exceed 10MBs. Submit only image formats: png, jpg, jpeg.</li>
              <li>Artworks must not be watermarked by any external source, cannot have any company logos, trademarks or otherwise display copyrighted content which does not belong to the Participant. Personal logos and signatures are allowed.</li>
              <li>The submitted artwork must be original, owned by the Participant, and should in no way violate intellectual property rights.</li>
              <li>If multiple submissions by the same company, person or group are among the top 3 nominees for a category, only the highest scoring artwork will be considered and compete for the prize.</li>
              <li>Participants should refrain from submitting any artworks containing nudity and/or violence.</li>
              <li>The Entry of a Participant must include a clickable link to CGTrader’s website (<a href="https://www.cgtrader.com" className="link--primary-dark">cgtrader.com</a>) on the portfolio website of the Participant, including but not limited to a personal website or/and online portfolio on other design communities (e.g. DeviantArt, Behance, Dribbble, etc.)</li>
              <li>Only Entries submitted from {rules.entry_validation.dates} will be considered as valid Entries.</li>
            </ul>
          </div>

          <div className="rules-section rules__winner-selection">
            <h3 className="rules-section__title is-smaller">Winner selection and announcement</h3>
            <p>
              The CGTrader {competitionTitle} has {categoriesNumber} categories - {rules.winner_selection.categories}.
              Each category will have one Winner and 2 runner-ups selected. A special Public Award will be given to two artworks that generate the most engagement.
            </p>
            <p>
              The Winners of the contest will be selected by CGTrader representatives and judges representing Awards media partners and sponsors.
              The judges will review all entries after the competition deadline and hold an individual voting to choose the top entries.
              The decision of judges is final. The submissions will be evaluated on the originality, quality and overall
              impression. <span className="text-semibold">Originality</span> reflects the Participants’ ability to approach the theme and details
              in a unique manner. <span className="text-semibold">Quality</span> includes detail and precision demonstrated in creating elements,
              composition, lighting, and overall mood of the artwork. The <span className="text-semibold">overall impression</span> takes into
              consideration composition, modeling, texturing, technical difficulty, detail as well as overall appearance
              and impact of the work.
            </p>
            <p>{rules.winner_selection.public_award}</p>
            <p>{rules.winner_selection.public_award2}</p>
            <p>
              If the top three submissions are deemed to be invalid for any reason, including but not limited to plagiarism, or not responding within fourty-eight hours after being contacted by the CGTrader team, alternative submissions may be selected as the winning entries. The Organizer will put reasonable effort in contacting the winners and is not responsible for failed attempts to contact a selected Participant. The Winners will be announced on the CGTrader Blog and informed personally via email.
            </p>
          </div>

          <div className="rules-section rules__prizes">
            <h3 className="rules-section__title is-smaller">Prizes</h3>
            <p>
              The prizes to be awarded to Winner and runner-ups of every category are listed on
              the <Link to={`/${competitionSlug}/${rules.prizes_page}`} className="link--primary-dark">dedicated page</Link> of the category.
              The monetary prize value of non-monetary prizes specified on our page is an approximate indication
              of the value of products and services provided.
            </p>
            <p>
              Prizes are provided by CGTrader and Sponsors. Prizes are non-transferable, not redeemable for cash and no prize substitution is possible. The Organizer and Sponsors reserve the right to substitute the Prize, or a component of the Prize, with another prize of equal or greater value at any time without prior notice.
            </p>
            <p>
              In case of embargoes or other import rules enforced by the law in specific countries, some Prizes may not be shipped to the country of residence of a Winner, the Winner will be notified of the restrictions encountered, but no cash compensation for the prize or alternative prize substituting the original will be provided.
            </p>
            <p>
              Some Prizes that have to be shipped may be taxed or subject to import duties depending on the country of residence of the Winner. The Winner is responsible for covering all such payments and duties. In case the Winner refuses to pay associated fees, the Prize will not be shipped and no cash compensation or alternate prize will be awarded.
            </p>
            <p>
              All Participants allow the Organizer and Sponsors to use his/her/its full name, submitted artwork, as well as information related to the Competition, for promotional and marketing purposes.
            </p>
          </div>

          <div className="rules-section rules__additional-information">
            <h3 className="rules-section__title is-smaller">Additional Information</h3>
            <p>The Organizer reserves the right to modify the Rules without informing the Participants beforehand.</p>
            <p>
              The Organizer and Sponsors are not held responsible for any damage or loss experienced as a result of participating in the competition by the Participant or any parties related to him/her/it.
            </p>
            <p>
              Email <span className="text-semibold">{rules.email}</span> with additional questions, comments and inquiries about the Competition.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

RulesPage.propTypes = {
  competitionSlug: PropTypes.string.isRequired,
}

export default RulesPage
